import { createAction } from 'redux-action';
import axios from 'axios';
import { AxiosCallError, AxiosCallRequest, AxiosCallSuccess, showErrorPage } from './commonActions';
import {
  startTestSuccess,
  fetchAssessment,
  setCurrentSectionId,
  updateSectionStatus,
} from './AssessmentActions';
import analytics from '../../services/analytics';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { isLoggedIn } from '../../helperFunctions.js';
import { history } from '../../index';
import { InviteStatus } from '../../store/reducers/InviteReducer';
import serializeSections from '../../Utils/sectionsSerializer';
import determineWorkflow from '../../Utils/determineWorkflow';

let ep = get_service_endpoint('assessment');

export const validateInviteSuccess = createAction('VALIDATE_INVITE_SUCCESS');
export const validateInviteFailure = createAction('VALIDATE_INVITE_FAILURE');
export const setAssessment = createAction('SET_ASSESSMENT');
export const getTestSettingsSuccess = createAction('GET_TEST_SETTINGS');
export const showModal = createAction('SHOW_MODAL');

export const fetchInvite = (invite_id, hash_value, company_id) => {
  return (dispatch, getState) => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/invites`;
    axios
      .get(url, {
        params: {
          invite_id: invite_id,
          hash_value: hash_value,
          company_id: company_id,
        },
      })
      .then(response => {
        if (response.success) {
          if (response.data.status === InviteStatus.CANCELLED) {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Invite Cancelled',
                errorMessage: 'Please contact CodeHall for further assistance',
              }),
            );
            history.push('/error');
          } else if (response.data.status === InviteStatus.EXPIRED) {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Invite Expired',
                errorMessage: 'Please contact CodeHall for further assistance',
              }),
            );
            history.push('/error');
          } else if (response.data.status === InviteStatus.SCHEDULED) {
            history.push('/schedule');
          } else if (response.data.status === InviteStatus.FINISHED) {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Test is Expired',
                errorMessage: 'Please contact CodeHall for further assistance',
              }),
            );
            history.push('/error');
          } else {
            dispatch(validateInviteSuccess(response.data));
          }
          dispatch(AxiosCallSuccess());
        }
      })
      .catch(error => {
        if (error.response) {
          if (error.response.status === 404) {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Invalid Invite Id',
                errorMessage: 'Please contact CodeHall for further assistance',
              }),
            );
          } else if (error.response.status === 400) {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Invalid Signature',
                errorMessage: 'Please contact CodeHall for further assistance',
              }),
            );
          } else {
            dispatch(
              showErrorPage({
                errorType: 'Application Error',
                errorHeading: 'Invalid Invite Id',
                errorMessage: 'An error occured while validating the invite',
              }),
            );
          }
          history.push('/error');
          dispatch(AxiosCallSuccess());
        }
      });
  };
};

export const startTest = (test_id, invite_id) => {
  return (dispatch, getState) => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/start_test`;
    axios
      .get(url, {
        params: {
          test_id: test_id,
          invite_id: invite_id,
        },
      })
      .then(response => {
        dispatch(startTestSuccess(response.data));
        // Emit analytics event for test start with explicit assessment_id
        try {
          analytics.trackStartTest(response.data.id, invite_id);
        } catch (e) {
          // best-effort: do not block the flow if analytics fails
          // console.debug('analytics.trackStartTest failed', e);
        }
        dispatch(AxiosCallSuccess());
        dispatch(setAssessment(response.data));
        // Set currentSectionId to current section's ID if sections exist
        const { sections: serializedSections, currentSection } = serializeSections(
          response.data.sections,
        );
        if (currentSection) {
          dispatch(setCurrentSectionId(currentSection.id));
        }
        const { enable_screen_and_camera } = getState().assessment.test_settings;
        if (enable_screen_and_camera) {
          history.push(`/recording/${response.data.id}`);
        } else {
          setTimeout(() => {
            const { sections, problems, mcqIds, id: assessmentId } = getState().assessment;

            // Use the workflow function with required parameters
            determineWorkflow({
              sections,
              problems,
              mcqIds,
              assessmentId,
              routerHistory: history,
              shouldRoute: true,
              currentSectionId: currentSection?.id,
            });
          }, 100);
        }
      })
      .catch(error => {
        if (error.response) {
          if (error.response.status === 401) {
            dispatch(
              showModal({
                name: 'unauthorized_access',
                type: 'error',
                title: 'Unauthorized access',
                content: 'Please make sure you are logged in as the user who received the invite',
              }),
            );
          } else if (error.response.status === 409) {
            dispatch(
              showModal({
                name: 'invite_used',
                type: 'error',
                title: 'Unable to start test',
                content: 'This invite has already been used for another test',
              }),
            );
          } else {
            dispatch(
              showModal({
                name: 'start_test_error',
                type: 'error',
                title: 'An error occured',
                content: 'Please contact CodeHall for further assistance',
              }),
            );
          }
        }
        dispatch(AxiosCallError({ message: 'An error occured while starting test' }));
      });
  };
};

export const fetchAssesmentForInvite = (test_id, invite_id) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/tests/${test_id}/invites/${invite_id}/assessments/`;
    axios
      .get(url)
      .then(response => {
        if (response.data.status !== 'PG') {
          dispatch(
            showErrorPage({
              errorType: 'Application Error',
              errorHeading: 'Assessment Complete',
              errorMessage: 'This assessment is complete and can no longer be viewed',
            }),
          );
          history.push('/error');
        }
        //sets the assesment in invite state
        dispatch(setAssessment(response.data));
        //updates the assessment in assessment sub state
        dispatch(fetchAssessment(response.data.id));
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        if (error.response) {
          if (error.response.status === 401) {
            dispatch(
              showModal({
                name: 'unauthorized_access',
                type: 'error',
                title: 'Unauthorized access',
                content: 'Please make sure you are logged in as the user who received the invite',
              }),
            );
          }
        }
        dispatch(AxiosCallError(error));
      });
  };
};
